import React, { useState, useEffect } from 'react';
import { PostRequest } from 'components/common';

const SheetPreview = ({
  dataSourceId,
  spreadsheetId,
  sheetName,
  sheetId,
  sheets = [],
  onSheetSelect,
  onCellSelect,
  selectedCell,
  maxRows = 50,
  maxColumns = 26
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dataSourceId || !spreadsheetId || !sheetName) {
      setData([]);
      return;
    }

    setLoading(true);
    setError(null);

    const endColumn = String.fromCharCode(64 + Math.min(maxColumns, 26)); // A=65, so 64+1=A
    const range = `A1:${endColumn}${maxRows}`;

    PostRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/fetch-range`,
      data: {
        spreadsheet_id: spreadsheetId,
        sheet_name: sheetName,
        range: range
      },
      callback: (result) => {
        setLoading(false);
        if (result.error) {
          setError(result.error);
        } else {
          setData(result.values || []);
        }
      }
    });
  }, [dataSourceId, spreadsheetId, sheetName, maxRows, maxColumns]);

  const getColumnLetter = (index) => {
    // Convert 0-based index to column letter (A, B, C, ... Z, AA, AB, etc.)
    let letter = '';
    let temp = index;
    while (temp >= 0) {
      letter = String.fromCharCode((temp % 26) + 65) + letter;
      temp = Math.floor(temp / 26) - 1;
    }
    return letter;
  };

  const getCellReference = (rowIndex, colIndex) => {
    return `${getColumnLetter(colIndex)}${rowIndex + 1}`;
  };

  const handleCellClick = (rowIndex, colIndex) => {
    const cellRef = getCellReference(rowIndex, colIndex);
    const value = data[rowIndex] && data[rowIndex][colIndex] !== undefined
      ? data[rowIndex][colIndex]
      : '';
    onCellSelect(cellRef, value);
  };

  const isCellSelected = (rowIndex, colIndex) => {
    return selectedCell === getCellReference(rowIndex, colIndex);
  };

  // Find the maximum number of columns across all rows
  const maxCols = data.reduce((max, row) => Math.max(max, row ? row.length : 0), 0);

  if (loading) {
    return (
      <div className="sheet-preview-loading" style={{ padding: '20px', textAlign: 'center' }}>
        <i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading_sheet_data')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="sheet-preview-error alert alert-danger">
        <i className="fa fa-exclamation-circle"></i> {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="sheet-preview-empty" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        {t('quote_builder.no_data_in_sheet')}
      </div>
    );
  }

  const sheetTabsStyle = {
    display: 'flex',
    background: '#f0f0f0',
    borderTop: '1px solid #ddd',
    padding: '0',
    overflowX: 'auto'
  };

  const sheetTabStyle = (isActive) => ({
    padding: '8px 16px',
    border: 'none',
    borderRight: '1px solid #ddd',
    background: isActive ? '#fff' : '#e8e8e8',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: isActive ? '500' : 'normal',
    color: isActive ? '#1a73e8' : '#5f6368',
    whiteSpace: 'nowrap',
    borderBottom: isActive ? '2px solid #1a73e8' : '2px solid transparent',
    marginBottom: isActive ? '-1px' : '0',
    transition: 'all 0.15s ease'
  });

  return (
    <div className="sheet-preview">
      <div className="sheet-preview-header" style={{ marginBottom: '10px' }}>
        <small className="text-muted">
          <i className="fa fa-hand-pointer-o"></i> {t('quote_builder.click_cell_to_select')}
        </small>
      </div>
      <div
        className="sheet-preview-container"
        style={{
          maxHeight: '300px',
          overflow: 'auto',
          border: '1px solid #ddd',
          borderRadius: sheets.length > 1 ? '4px 4px 0 0' : '4px'
        }}
      >
        <table
          className="table table-bordered table-sm sheet-preview-table"
          style={{
            marginBottom: 0,
            fontSize: '12px',
            tableLayout: 'auto'
          }}
        >
          <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
            <tr>
              <th style={{
                minWidth: '30px',
                textAlign: 'center',
                background: '#e9e9e9',
                fontWeight: 'bold',
                position: 'sticky',
                left: 0,
                zIndex: 2
              }}></th>
              {[...Array(maxCols)].map((_, colIdx) => (
                <th
                  key={colIdx}
                  style={{
                    minWidth: '60px',
                    textAlign: 'center',
                    background: '#e9e9e9',
                    fontWeight: 'bold'
                  }}
                >
                  {getColumnLetter(colIdx)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td style={{
                  textAlign: 'center',
                  background: '#e9e9e9',
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1
                }}>
                  {rowIdx + 1}
                </td>
                {[...Array(maxCols)].map((_, colIdx) => {
                  const cellValue = row && row[colIdx] !== undefined ? row[colIdx] : '';
                  const isSelected = isCellSelected(rowIdx, colIdx);
                  return (
                    <td
                      key={colIdx}
                      onClick={() => handleCellClick(rowIdx, colIdx)}
                      style={{
                        cursor: 'pointer',
                        minWidth: '60px',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '4px 8px',
                        background: isSelected ? '#007bff' : 'white',
                        color: isSelected ? 'white' : 'inherit',
                        border: isSelected ? '2px solid #0056b3' : undefined,
                        transition: 'background-color 0.15s ease'
                      }}
                      title={cellValue}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.background = '#e3f2fd';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sheets.length > 1 && (
        <div className="sheet-tabs" style={sheetTabsStyle}>
          {sheets.map(sheet => (
            <button
              key={sheet.id}
              type="button"
              style={sheetTabStyle(sheet.id === sheetId)}
              onClick={() => onSheetSelect && onSheetSelect(sheet)}
              onMouseEnter={(e) => {
                if (sheet.id !== sheetId) {
                  e.target.style.background = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (sheet.id !== sheetId) {
                  e.target.style.background = '#e8e8e8';
                }
              }}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SheetPreview;
